import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Edit, Plus, Route as RouteIcon, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { EmptyState, PageHeader, StatusPill } from "@/components/uiext";
import { DialTester } from "@/components/DialTester";
import { InlineEditorShell } from "@/components/InlineEditorShell";
import { useConfirm } from "@/lib/confirm.jsx";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  direction: "outbound",
  prefix: "",
  priority: 100,
  enabled: true,
  scope: "global",
  customer_id: "",
  reseller_id: "",
  trunk_id: "",
  failover_trunk_ids: [],
  prefix_strip: 0,
  prefix_add: "",
};

function DialplanPreview({ sample, strip, add }) {
  const stripped = strip > 0 ? String(sample).slice(strip) : String(sample);
  const finalNumber = `${add || ""}${stripped}`;
  return (
    <div className="rounded-md bg-muted/40 px-2 py-1.5 text-[11px] font-mono flex items-center gap-2">
      <span className="text-muted-foreground">preview:</span>
      <span>{sample || "—"}</span>
      <span className="text-muted-foreground">→</span>
      <span className="text-[hsl(var(--accent))] font-semibold">
        {finalNumber || "—"}
      </span>
    </div>
  );
}

export default function RoutingPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [direction, setDirection] = useState("outbound");
  const [items, setItems] = useState([]);
  const [trunks, setTrunks] = useState([]);
  const [customers, setCustomers] = useState([]);
  // Phase N+ — inline editor state replaces the old <Dialog>. Two modes:
  //   null          → list view
  //   { form, id }  → inline editor (id === null for new)
  const [editor, setEditor] = useState(null);

  const load = async () => {
    const [r, tk, c] = await Promise.all([
      api.get(`/routes?direction=${direction}`),
      api.get("/trunks"),
      api.get("/tenants?kind=customer"),
    ]);
    setItems(r.data);
    setTrunks(tk.data);
    setCustomers(c.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);

  const save = async () => {
    if (!editor) return;
    try {
      const form = editor.form;
      const body = {
        ...form,
        priority: Number(form.priority || 100),
        customer_id: form.customer_id || null,
        reseller_id: form.reseller_id || null,
        trunk_id: form.trunk_id || null,
      };
      if (editor.id) await api.put(`/routes/${editor.id}`, body);
      else await api.post("/routes", body);
      toast.success(t("common.saved"));
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };
  const del = async (r) => {
    const _ok = await confirm({
      title: t("confirm.deleteRoute", { name: r.name }),
      danger: true,
      confirmLabel: t("common.delete") || "Delete",
    });
    if (!_ok) return;
    await api.delete(`/routes/${r.id}`);
    load();
  };
  const editRoute = (r) => setEditor({ id: r.id, form: { ...r } });
  const newRoute = () => setEditor({ id: null, form: { ...EMPTY, direction } });

  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    await api.post("/routes/reorder", { ordered_ids: arr.map((r) => r.id) });
    load();
  };

  // Phase N+ — 3CX/Vodia-style inline editor takes over the whole page.
  if (editor !== null) {
    const form = editor.form;
    const setForm = (patch) => setEditor({ ...editor, form: typeof patch === "function" ? patch(form) : patch });
    return (
      <InlineEditorShell
        crumb={t("pages.routing.title")}
        title={
          editor.id
            ? `${t("pages.routing.dialogEdit")}${form.name?.trim() ? `: ${form.name.trim()}` : ""}`
            : t("pages.routing.dialogNew")
        }
        onCancel={() => setEditor(null)}
        onSave={save}
        saveDisabled={!form.name.trim()}
        testidRoot="route-editor"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("fields.name")}</Label>
              <Input
                data-testid="route-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label>{t("fields.direction")}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.direction}
                onChange={(e) => setForm({ ...form, direction: e.target.value })}
              >
                <option value="outbound">{t("pages.routing.outbound")}</option>
                <option value="inbound">{t("pages.routing.inbound")}</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("fields.prefix")}</Label>
              <Input
                data-testid="route-prefix"
                value={form.prefix}
                onChange={(e) => setForm({ ...form, prefix: e.target.value })}
                placeholder={t("pages.routing.prefixPlaceholder")}
              />
            </div>
            <div>
              <Label>{t("fields.priority")}</Label>
              <Input
                type="number"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              />
            </div>
          </div>
          {form.direction === "outbound" ? (
            <>
              <div>
                <Label>{t("fields.targetTrunk")}</Label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.trunk_id || ""}
                  onChange={(e) => setForm({ ...form, trunk_id: e.target.value })}
                >
                  <option value="">{t("pages.routing.pickTrunk")}</option>
                  {trunks.map((tk) => (
                    <option key={tk.id} value={tk.id}>
                      {tk.name} ({tk.host})
                    </option>
                  ))}
                </select>
              </div>

              {/* Failover trunks: ordered list of fallbacks tried when
                  the primary is marked unreachable by the OPTIONS probe. */}
              <div className="rounded-md border border-border p-3 space-y-2">
                <div className="text-xs font-medium">
                  {t("pages.routing.failoverTitle")}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {t("pages.routing.failoverHelp")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(form.failover_trunk_ids || []).map((fid, i) => {
                    const tk = trunks.find((x) => x.id === fid);
                    return (
                      <span
                        key={fid}
                        className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--accent))]/15 px-2 py-0.5 text-[11px] font-mono"
                        data-testid={`route-failover-chip-${i}`}
                      >
                        #{i + 1} {tk ? tk.name : fid}
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-[hsl(var(--danger))]"
                          onClick={() =>
                            setForm({
                              ...form,
                              failover_trunk_ids: form.failover_trunk_ids.filter(
                                (x) => x !== fid
                              ),
                            })
                          }
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value=""
                  onChange={(e) => {
                    const fid = e.target.value;
                    if (!fid) return;
                    if ((form.failover_trunk_ids || []).includes(fid)) return;
                    if (form.trunk_id === fid) return;
                    setForm({
                      ...form,
                      failover_trunk_ids: [...(form.failover_trunk_ids || []), fid],
                    });
                  }}
                  data-testid="route-failover-add"
                >
                  <option value="">{t("pages.routing.addFailover")}</option>
                  {trunks
                    .filter(
                      (tk) =>
                        tk.id !== form.trunk_id &&
                        !(form.failover_trunk_ids || []).includes(tk.id)
                    )
                    .map((tk) => (
                      <option key={tk.id} value={tk.id}>
                        {tk.name} ({tk.host})
                      </option>
                    ))}
                </select>
              </div>

              {/* Dial-plan: prefix manipulation. Strip leading N then prepend. */}
              <div className="rounded-md border border-border p-3 space-y-2">
                <div className="text-xs font-medium">
                  {t("pages.routing.dialplanTitle")}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {t("pages.routing.dialplanHelp")}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">
                      {t("pages.routing.prefixStrip")}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="20"
                      value={form.prefix_strip ?? 0}
                      onChange={(e) =>
                        setForm({ ...form, prefix_strip: Number(e.target.value) || 0 })
                      }
                      data-testid="route-prefix-strip"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {t("pages.routing.prefixStripHint")}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">
                      {t("pages.routing.prefixAdd")}
                    </Label>
                    <Input
                      value={form.prefix_add ?? ""}
                      onChange={(e) =>
                        setForm({ ...form, prefix_add: e.target.value })
                      }
                      placeholder="e.g. +1, 9011"
                      data-testid="route-prefix-add"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {t("pages.routing.prefixAddHint")}
                    </p>
                  </div>
                </div>
                {/* Live preview */}
                <DialplanPreview
                  sample={form.prefix || "11234567890"}
                  strip={form.prefix_strip || 0}
                  add={form.prefix_add || ""}
                />
              </div>
            </>
          ) : (
            <div>
              <Label>{t("fields.targetCustomer")}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.customer_id || ""}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
              >
                <option value="">{t("pages.routing.pickCustomer")}</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} — {c.domain}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </InlineEditorShell>
    );
  }

  return (
    <div data-testid="page-routing">
      <PageHeader
        title={t("pages.routing.title")}
        subtitle={t("pages.routing.subtitle")}
        icon={RouteIcon}
        actions={
          <Button onClick={newRoute} data-testid="new-route-btn">
            <Plus className="h-4 w-4 mr-1" /> {t("pages.routing.newBtn")}
          </Button>
        }
      />
      <DialTester customers={customers} />
      <Tabs value={direction} onValueChange={setDirection}>
        <TabsList data-testid="routing-tabs">
          <TabsTrigger value="outbound" data-testid="tab-outbound">
            {t("pages.routing.outbound")}
          </TabsTrigger>
          <TabsTrigger value="inbound" data-testid="tab-inbound">
            {t("pages.routing.inbound")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value={direction} forceMount>
          {items.length === 0 ? (
            <EmptyState
              title={
                direction === "outbound"
                  ? t("pages.routing.emptyOutbound")
                  : t("pages.routing.emptyInbound")
              }
              description={
                direction === "outbound"
                  ? t("pages.routing.emptyOutboundDesc")
                  : t("pages.routing.emptyInboundDesc")
              }
              action={
                <Button onClick={newRoute}>
                  <Plus className="h-4 w-4 mr-1" /> {t("pages.routing.emptyAction")}
                </Button>
              }
            />
          ) : (
            <div className="rounded-lg border border-border bg-card overflow-x-auto mt-3">
              <Table>
                <TableHeader className="sticky-table-head">
                  <TableRow>
                    <TableHead className="w-[60px]">#</TableHead>
                    <TableHead>{t("fields.name")}</TableHead>
                    <TableHead>{t("pages.routing.colPrefix")}</TableHead>
                    <TableHead>{t("pages.routing.colScope")}</TableHead>
                    <TableHead>{t("pages.routing.colTarget")}</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead className="w-[160px] text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r, idx) => (
                    <TableRow
                      key={r.id}
                      className="row-hover cursor-pointer"
                      data-testid={`route-row-${r.id}`}
                      onClick={(e) => {
                        if (e.target.closest("button")) return;
                        editRoute(r);
                      }}
                    >
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.priority}
                      </TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.prefix ? (
                          r.prefix
                        ) : (
                          <span className="text-muted-foreground">{t("common.any")}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusPill kind={r.scope === "global" ? "info" : "ok"}>
                          {r.scope}
                        </StatusPill>
                      </TableCell>
                      <TableCell className="text-sm">
                        {direction === "outbound"
                          ? trunks.find((tk) => tk.id === r.trunk_id)?.name || "—"
                          : customers.find((c) => c.id === r.customer_id)?.name || "—"}
                      </TableCell>
                      <TableCell>
                        <StatusPill kind={r.enabled ? "ok" : "offline"}>
                          {r.enabled ? t("common.enabled") : t("common.disabled")}
                        </StatusPill>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap space-x-1">
                        <Button variant="ghost" size="icon" onClick={() => move(idx, -1)}>
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => move(idx, 1)}>
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => editRoute(r)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-[hsl(var(--danger))]"
                          onClick={() => del(r)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
